/**
 * Magicstar Theme — Cart Drawer JS
 *
 * Uses Shopify's AJAX Cart API (/cart/*.js) for all cart operations.
 * This is critical for:
 *   1. Native Shopify abandon cart tracking (cart lives on Shopify's servers)
 *   2. Meta/TikTok CAPI deduplication (Shopify owns cart events)
 *   3. Shopify Flows automation (triggered by cart events)
 *
 * Meta Pixel AddToCart is fired AFTER successful /cart/add.js response.
 * This eliminates false positives from failed add-to-cart attempts.
 *
 * Event deduplication:
 *   Browser pixel fires with unique event_id.
 *   Shopify Meta Sales Channel CAPI uses the same event_id (via checkout).
 *   Shopify's dedup window = 7 days by default.
 */

(function () {
  'use strict';

  var cartDrawer = document.getElementById('cart-drawer');
  var cartToggleBtns = document.querySelectorAll('.js-cart-toggle');
  var cartCloseBtns = document.querySelectorAll('.js-cart-close');
  var drawerItems = document.getElementById('drawer-items');
  var drawerFooter = document.getElementById('drawer-footer');
  var drawerSubtotal = document.getElementById('drawer-subtotal');

  // ── Open / Close ─────────────────────────────────────────────────────────

  function openCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    refreshCart(); // Always refresh when opening
  }

  function closeCartDrawer() {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  cartToggleBtns.forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      if (cartDrawer.classList.contains('is-open')) {
        closeCartDrawer();
      } else {
        openCartDrawer();
      }
    });
  });

  cartCloseBtns.forEach(function (btn) {
    btn.addEventListener('click', closeCartDrawer);
  });

  document.addEventListener('click', function (e) {
    var closeTrigger = e.target.closest('.js-cart-close');
    if (!closeTrigger) return;
    if (!cartDrawer || !cartDrawer.contains(closeTrigger)) return;
    e.preventDefault();
    closeCartDrawer();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && cartDrawer && cartDrawer.classList.contains('is-open')) {
      closeCartDrawer();
    }
  });

  // ── Fetch Cart State ─────────────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  // ── Refresh Cart Drawer HTML ─────────────────────────────────────────────

  function refreshCart() {
    return fetchCart().then(function (cart) {
      renderCartDrawer(cart);
      window.updateCartCount && window.updateCartCount(cart.item_count);
      updateShippingBar(cart.total_price);
    });
  }

  function renderCartDrawer(cart) {
    if (!drawerItems) return;

    if (cart.item_count === 0) {
      drawerItems.innerHTML = '<div class="cart-drawer__empty" id="drawer-empty">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ddd" stroke-width="1.5" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' +
        '<p>Your cart is empty</p>' +
        '<a href="/" class="btn btn--outline js-cart-close">Continue Shopping</a>' +
        '</div>';
      if (drawerFooter) drawerFooter.style.display = 'none';
      return;
    }

    if (drawerFooter) drawerFooter.style.display = '';

    var html = cart.items.map(function (item, idx) {
      var comparePrice = item.compare_at_price > item.price
        ? '<span style="text-decoration:line-through;color:#aaa;font-size:12px;margin-right:4px;">' + formatMoney(item.compare_at_price) + '</span>'
        : '';
      var variantTitle = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<p class="drawer-item__variant">' + escapeHtml(item.variant_title) + '</p>'
        : '';
      return '<div class="drawer-item" data-line-item-id="' + escapeHtml(item.key) + '" data-line-index="' + (idx + 1) + '">' +
        '<a href="' + escapeHtml(item.url) + '" class="drawer-item__img-link">' +
        (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy" width="80" height="80" class="drawer-item__img">' : '') +
        '</a>' +
        '<div class="drawer-item__info">' +
        '<div class="drawer-item__header">' +
        '<a href="' + escapeHtml(item.url) + '" class="drawer-item__title">' + escapeHtml(item.product_title) + '</a>' +
        '<button type="button" class="drawer-item__remove btn-reset js-drawer-remove" data-line="' + (idx + 1) + '" aria-label="Remove">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button></div>' +
        variantTitle +
        '<div class="drawer-item__footer">' +
        '<div class="quantity-selector quantity-selector--small">' +
        '<button type="button" class="quantity-selector__btn js-drawer-qty-minus btn-reset" data-line="' + (idx + 1) + '" aria-label="Decrease"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg></button>' +
        '<input type="number" value="' + item.quantity + '" min="0" class="quantity-selector__input js-drawer-qty-input" data-line="' + (idx + 1) + '" aria-label="Quantity">' +
        '<button type="button" class="quantity-selector__btn js-drawer-qty-plus btn-reset" data-line="' + (idx + 1) + '" aria-label="Increase"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg></button>' +
        '</div>' +
        '<span class="drawer-item__price">' + comparePrice + formatMoney(item.final_line_price) + '</span>' +
        '</div></div></div>';
    }).join('');

    drawerItems.innerHTML = html;

    // Update subtotal
    if (drawerSubtotal) {
      drawerSubtotal.textContent = formatMoney(cart.total_price);
    }

    // Re-bind events after re-render
    bindDrawerEvents();
  }

  // ── Update Shipping Bar ─────────────────────────────────────────────────

  function updateShippingBar(totalPrice) {
    var bar = document.getElementById('drawer-shipping-bar');
    if (!bar) return;
    var fill = document.getElementById('drawer-shipping-fill');
    var text = document.getElementById('drawer-shipping-text');
    var thresholdEl = document.getElementById('drawer-shipping-bar');
    var threshold = (thresholdEl && thresholdEl.getAttribute('data-threshold'))
      ? parseInt(thresholdEl.getAttribute('data-threshold'), 10)
      : 20000; // fallback: 200 AED in cents
    var remaining = threshold - totalPrice;
    var progress = Math.min(100, Math.round((totalPrice / threshold) * 100));
    if (fill) fill.style.width = progress + '%';
    if (text) {
      if (remaining > 0) {
        text.innerHTML = 'You\'re <strong>' + formatMoney(remaining) + '</strong> away from free shipping!';
      } else {
        text.innerHTML = '🎉 You\'ve unlocked free shipping!';
        text.className = 'drawer-shipping-bar__text';
      }
    }
  }

  // ── Cart Operations ──────────────────────────────────────────────────────

  /**
   * Add item to Shopify cart via AJAX.
   * On success: fires Meta Pixel + TikTok AddToCart events, refreshes drawer.
   */
  window.addToCart = function (variantId, quantity, productTitle, price, productUrl) {
    quantity = quantity || 1;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart add failed: ' + r.status);
        return r.json();
      })
      .then(function (item) {
        // ── Fire tracking ONLY after confirmed cart add ──────────────────
        fireAddToCartTracking({
          variantId: String(variantId),
          productTitle: productTitle || item.product_title,
          price: (price || item.price) / 100,
          quantity: quantity
        });

        // Refresh drawer & count
        return refreshCart();
      });
  };

  function updateLineQuantity(line, quantity) {
    var updates = {};
    updates[line] = quantity;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refreshCart(); });
  }

  // ── Event Binding ────────────────────────────────────────────────────────

  function bindDrawerEvents() {
    // Remove line
    document.querySelectorAll('.js-drawer-remove').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var line = parseInt(btn.getAttribute('data-line'), 10);
        updateLineQuantity(line, 0);
      });
    });

    // Qty minus
    document.querySelectorAll('.js-drawer-qty-minus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var line = parseInt(btn.getAttribute('data-line'), 10);
        var input = btn.parentElement.querySelector('.js-drawer-qty-input');
        var current = parseInt(input.value, 10);
        if (current <= 1) {
          updateLineQuantity(line, 0);
        } else {
          updateLineQuantity(line, current - 1);
        }
      });
    });

    // Qty plus
    document.querySelectorAll('.js-drawer-qty-plus').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var line = parseInt(btn.getAttribute('data-line'), 10);
        var input = btn.parentElement.querySelector('.js-drawer-qty-input');
        var current = parseInt(input.value, 10);
        updateLineQuantity(line, current + 1);
      });
    });

    // Qty direct input
    document.querySelectorAll('.js-drawer-qty-input').forEach(function (input) {
      var debounce;
      input.addEventListener('change', function () {
        clearTimeout(debounce);
        debounce = setTimeout(function () {
          var line = parseInt(input.getAttribute('data-line'), 10);
          var qty = parseInt(input.value, 10);
          if (isNaN(qty) || qty < 0) qty = 0;
          updateLineQuantity(line, qty);
        }, 400);
      });
    });
  }

  // Run initial binding
  bindDrawerEvents();

  // ── Quick Add buttons (product cards) ────────────────────────────────────

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.js-quick-add');
    if (!btn) return;
    e.preventDefault();

    var variantId = btn.getAttribute('data-variant-id');
    var productTitle = btn.getAttribute('data-product-title');
    var price = parseFloat(btn.getAttribute('data-product-price')) || 0;
    var originalText = btn.querySelector('span') ? btn.querySelector('span').textContent : btn.textContent;

    btn.classList.add('btn--loading');
    btn.setAttribute('disabled', '');

    window.addToCart(variantId, 1, productTitle, price)
      .then(function () {
        btn.classList.remove('btn--loading');
        btn.removeAttribute('disabled');
        window.showCartToast && window.showCartToast('✓ ' + productTitle + ' added to cart');
        openCartDrawer();
      })
      .catch(function (err) {
        btn.classList.remove('btn--loading');
        btn.removeAttribute('disabled');
        window.showCartToast && window.showCartToast('Something went wrong. Please try again.');
        console.error('[Cart] Quick add failed:', err);
      });
  });

  // ── Cart page quantity buttons ────────────────────────────────────────────
  // All operations use AJAX Cart API — no form submits — for consistent
  // behaviour and to keep cart state on Shopify's servers for automation.

  function updateCartPageDOM(cart) {
    // Update cart item rows & subtotal on the /cart page without a full reload
    var subtotalEl = document.getElementById('cart-subtotal');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    window.updateCartCount && window.updateCartCount(cart.item_count);
    updateShippingBar(cart.total_price);
    // If all items removed, reload to show empty cart template cleanly
    if (cart.item_count === 0) { window.location.reload(); }
  }

  document.addEventListener('click', function (e) {
    // Cart page qty minus
    var minusBtn = e.target.closest('.js-qty-minus');
    if (minusBtn) {
      e.preventDefault();
      var lineIdx = parseInt(minusBtn.getAttribute('data-line'), 10);
      var qtyInput = minusBtn.parentElement.querySelector('.quantity-selector__input');
      var current = parseInt(qtyInput.value, 10);
      var newQty = Math.max(0, current - 1);
      qtyInput.value = newQty;
      clearTimeout(window.__cartPageDebounce);
      window.__cartPageDebounce = setTimeout(function () {
        updateLineQuantity(lineIdx, newQty).then(updateCartPageDOM);
      }, 300);
      return;
    }

    // Cart page qty plus
    var plusBtn = e.target.closest('.js-qty-plus');
    if (plusBtn) {
      e.preventDefault();
      var lineIdx = parseInt(plusBtn.getAttribute('data-line'), 10);
      var qtyInput = plusBtn.parentElement.querySelector('.quantity-selector__input');
      var current = parseInt(qtyInput.value, 10);
      var newQty = current + 1;
      qtyInput.value = newQty;
      clearTimeout(window.__cartPageDebounce);
      window.__cartPageDebounce = setTimeout(function () {
        updateLineQuantity(lineIdx, newQty).then(updateCartPageDOM);
      }, 300);
      return;
    }

    // Cart page remove
    var removeBtn = e.target.closest('.js-remove-line');
    if (removeBtn) {
      e.preventDefault();
      var lineIdx = parseInt(removeBtn.getAttribute('data-line'), 10);
      updateLineQuantity(lineIdx, 0).then(function (cart) {
        // Remove the row from DOM immediately, then sync count
        var row = removeBtn.closest('[data-cart-item]') || removeBtn.closest('tr');
        if (row) row.remove();
        updateCartPageDOM(cart);
      });
    }
  });

  // Cart page direct qty input
  document.addEventListener('change', function (e) {
    if (!e.target.matches('.js-cart-qty-input')) return;
    var input = e.target;
    var lineIdx = parseInt(input.getAttribute('data-line'), 10);
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) { qty = 0; input.value = 0; }
    clearTimeout(window.__cartPageDebounce);
    window.__cartPageDebounce = setTimeout(function () {
      updateLineQuantity(lineIdx, qty).then(updateCartPageDOM);
    }, 400);
  });

  // ── Tracking: Meta Pixel AddToCart ───────────────────────────────────────

  function fireAddToCartTracking(params) {
    // Tracking removed from theme code. Use Shopify native integrations / customer events instead.
    return;
  }

  // ── Utility ──────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10) || 0;
    return 'AED ' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Expose for other scripts
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;

})();
